<template>
    <Row hoverable class="EntityActionsTableRow" @click="$emit('review', entityAction.id)">
        <Cell class="EntityActionsTableRow__value">
            <span v-if="actionValue != null && actionValue !== ''" class="EntityActionsTableRow__valueText">
                <span
                    v-for="(segment, index) of valueSegments"
                    :key="index"
                    :class="{ 'EntityActionsTableRow__valueMatch': segment.matched }"
                >{{ segment.text }}</span>
            </span>
            <EmptyValueDashSymbol v-else />
        </Cell>
        <Cell class="EntityActionsTableRow__type">
            <LabelBadge v-if="actionScope" :color="actionScopeColor" size="sm">
                {{ actionScope }}
            </LabelBadge>
            <EmptyValueDashSymbol v-else />
        </Cell>
        <Cell class="EntityActionsTableRow__entity">
            <template v-if="entityDisplayName">
                {{ entityDisplayName }}
            </template>
            <EmptyValueDashSymbol v-else />
        </Cell>
        <template v-if="isCompletedTab">
            <Cell class="EntityActionsTableRow__outcome">
                <template v-if="outcomeLabel">
                    {{ outcomeLabel }}
                </template>
                <EmptyValueDashSymbol v-else />
            </Cell>
            <Cell class="EntityActionsTableRow__resolvedBy">
                <template v-if="resolvedBy">
                    {{ resolvedBy }}
                </template>
                <EmptyValueDashSymbol v-else />
            </Cell>
            <Cell class="EntityActionsTableRow__resolvedDate">
                <template v-if="resolvedDate">
                    {{ resolvedDate }}
                </template>
                <EmptyValueDashSymbol v-else />
            </Cell>
            <Cell class="EntityActionsTableRow__reverted">
                {{ revertedLabel }}
            </Cell>
        </template>
        <template v-else>
            <Cell class="EntityActionsTableRow__actionRequired">
                <span
                    v-if="actionRequiredParts.prefix"
                    class="EntityActionsTableRow__actionRequiredPrefix"
                >{{ actionRequiredParts.prefix }} - </span>
                <span class="EntityActionsTableRow__actionRequiredField">
                    {{ actionRequiredParts.field }}
                </span>
            </Cell>
            <Cell class="EntityActionsTableRow__dateCreated">
                {{ formattedCreatedAt }}
            </Cell>
        </template>
    </Row>
</template>

<style lang="scss">
.EntityActionsTableRow {
    cursor: pointer;

    &__emailText {
        font-size: 11px;
        display: block;
        color: #99adbd;
    }

    &__valueMatch {
        /* Weight and a tinted ground rather than solid colour, so a long wrapped value stays
           readable when most of it matches. */
        font-weight: 600;
        background-color: var(--eidTrustGreen-25);
        border-radius: 2px;
    }

    &__actionRequiredPrefix {
        /* The leading half of the label repeats down the column; the field name after it is what
           tells rows apart, so the prefix recedes and the field leads. */
        font-size: var(--text-caption-size, 11px);
        color: var(--eidTechGray-75);
    }

    &__actionRequiredField {
        font-weight: 600;
    }
}
</style>

<script lang="ts">
    import { PropType } from 'vue';

    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { faMinus } from '@fortawesome/free-solid-svg-icons';

    import LabelBadge from '@evidentid/dashboard-commons/components/LabelBadge/LabelBadge.vue';
    import { Cell, Row } from '@evidentid/dashboard-commons/components/Table';
    import { TprmRequirementDefinition } from '@evidentid/tprm-portal-lib/models/common';
    import { ActionReview, ActionsTab } from '@evidentid/tprm-portal-lib/models/entity-actions-review';

    import EmptyValueDashSymbol
        from '@/modules/entity-actions-review/components/EmptyValueDashSymbol/EmptyValueDashSymbol.vue';
    import { EntityActionScopeType } from '@/modules/entity-actions-review/types';
    import {
        getOutcomeLabel,
        getResolvedBy,
        getResolvedDate,
        getRevertedLabel,
    } from '@/modules/entity-actions-review/utils/completedActionFields';
    import { getActionRequired } from '@/modules/entity-actions-review/utils/getActionRequired';
    import { getActionScopeType } from '@/modules/entity-actions-review/utils/getActionScopeType';
    import { getActionValue } from '@/modules/entity-actions-review/utils/getActionValue';
    import { HighlightSegment, highlightSegments } from '@/modules/entity-actions-review/utils/highlightSegments';
    import { ActionRequiredParts, splitActionRequired } from '@/modules/entity-actions-review/utils/splitActionRequired';
    import { formatDateTime } from '@/utils/formatDateTime';

    @Component({
        emits: [ 'review' ],
        components: { EmptyValueDashSymbol, Cell, LabelBadge, Row },
    })
    export default class EntityActionsTableRow extends Vue {
        @Prop({ type: Object })
        private entityAction!: ActionReview;

        @Prop({ type: String as PropType<ActionsTab>, default: ActionsTab.pending })
        private tab!: ActionsTab;

        @Prop({ type: Object, default: {} })
        private requirementDefinitions!: Record<string, TprmRequirementDefinition>;

        @Prop({ type: String, default: '' })
        private searchTerm!: string;

        private faMinus = faMinus;
        private readonly EntityActionScopeType = EntityActionScopeType;

        private get isCompletedTab(): boolean {
            return this.tab === ActionsTab.completed;
        }

        private get actionRequired(): string {
            return getActionRequired(this.entityAction.action, this.requirementDefinitions);
        }

        private get actionValue(): any {
            return getActionValue(this.entityAction);
        }

        private get actionScope(): string {
            return getActionScopeType(this.entityAction.action) || '';
        }

        // Global actions resolve for every entity at once, so they carry the heavier colour; an
        // entity-scoped one affects only the row it sits on.
        private get actionScopeColor(): string {
            return this.actionScope === EntityActionScopeType.global ? 'warning' : 'info';
        }

        private get valueSegments(): HighlightSegment[] {
            return highlightSegments(String(this.actionValue ?? ''), this.searchTerm);
        }

        private get actionRequiredParts(): ActionRequiredParts {
            return splitActionRequired(this.actionRequired);
        }

        private get entityDisplayName(): string {
            return this.entityAction.entityNames?.displayName || '';
        }

        private get outcomeLabel(): string {
            return getOutcomeLabel(this.entityAction);
        }

        private get resolvedBy(): string {
            return getResolvedBy(this.entityAction);
        }

        // Reads resolution.resolvedAt rather than completedAt: completedAt is null on a
        // reverted action, so deriving this from completedAt would leave every reverted
        // row with a blank date in this column.
        private get resolvedDate(): string {
            return getResolvedDate(this.entityAction);
        }

        private get revertedLabel(): string {
            return getRevertedLabel(this.entityAction);
        }

        private get formattedCreatedAt(): string {
            return formatDateTime(this.entityAction?.createdAt);
        }
    }
</script>
